import { Link, useParams, useSearchParams } from 'react-router-dom';
import * as M from '@mantine/core';
import { IconArrowsSort, IconSortAscending, IconSortDescending } from '@tabler/icons-react';

import { textOrDash } from '../../../../../../../shared/utils/formatText';
import { formatDate, handleChangeLocaleTime } from '../../../../../../../shared/utils/time';

import HoverCardText from '../../../../../../ui/hoverCardText';
import StatusBadge from '../../../Common/ui/StatusBadge';
import { buildSearchString } from '../../../utils/buildSearchString';

import type { IAiAgentAsCsListItem } from '../../../../../../../shared/apis/ai-agent/ascs/type';
import type { ApiResponse } from '../../../../../../../shared/types/response/types';
import type { AsCsOrder, AsCsReceiptSortField } from '../types';

interface IAsCsListTableProps {
  data?: ApiResponse<IAiAgentAsCsListItem>;
  page: number;
  size: number;
  sortBy: AsCsReceiptSortField | null;
  order: AsCsOrder | null;
  isLoading: boolean;
  isError: boolean;
  onPageChange: (page: number) => void;
  onSortChange: (sortBy: AsCsReceiptSortField, order: AsCsOrder | null) => void;
}

interface ISortHeaderProps {
  label: string;
  field: AsCsReceiptSortField;
  sortBy: AsCsReceiptSortField | null;
  order: AsCsOrder | null;
  onSortChange: (sortBy: AsCsReceiptSortField, order: AsCsOrder | null) => void;
}

const getNextOrder = (currentOrder: AsCsOrder | null): AsCsOrder | null => {
  if (currentOrder === 'DESC') return 'ASC';
  if (currentOrder === 'ASC') return null;

  return 'DESC';
};

const getModelLabel = (row: IAiAgentAsCsListItem): string => {
  if (row.modelLabel) {
    return row.modelLabel;
  }

  if (row.modelName && row.model) {
    return `${row.modelName} (${row.model})`;
  }

  return textOrDash(row.modelName || row.model);
};

function SortHeader({ label, field, sortBy, order, onSortChange }: ISortHeaderProps) {
  const isActive = sortBy === field;
  const nextOrder = getNextOrder(isActive ? order : null);
  const Icon = !isActive ? IconArrowsSort : order === 'ASC' ? IconSortAscending : IconSortDescending;

  return (
    <M.UnstyledButton
      onClick={() => onSortChange(field, nextOrder)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 'inherit',
        fontWeight: 600,
        lineHeight: 'inherit',
      }}
    >
      <span>{label}</span>
      <M.ActionIcon component="span" size="xs" variant="subtle" color={isActive ? 'blue' : 'gray'}>
        <Icon size={14} />
      </M.ActionIcon>
    </M.UnstyledButton>
  );
}

export default function AsCsListTable(props: IAsCsListTableProps) {
  const { data, page, size, sortBy, order, isLoading, isError, onPageChange, onSortChange } = props;
  const { companyId } = useParams<{ companyId: string }>();
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || '';
  const rows = data?.result ?? [];
  const totalCount = data?.count ?? 0;
  const totalPage = Math.ceil(totalCount / size);
  const emptyMessage = isLoading ? '불러오는 중입니다.' : isError ? 'AS/CS 정보를 불러오지 못했습니다.' : '검색 결과가 없습니다.';

  return (
    <div>
      <M.Text fw={600} mb="md">
        AS/CS 목록
      </M.Text>
      <M.Table.ScrollContainer minWidth={1320}>
        <M.Table stickyHeader highlightOnHover>
          <M.Table.Thead>
            <M.Table.Tr>
              <M.Table.Th>
                <SortHeader label="관리번호" field="manageNo" sortBy={sortBy} order={order} onSortChange={onSortChange} />
              </M.Table.Th>
              <M.Table.Th>
                <SortHeader label="접수일시" field="receiptDt" sortBy={sortBy} order={order} onSortChange={onSortChange} />
              </M.Table.Th>
              <M.Table.Th>접수번호</M.Table.Th>
              <M.Table.Th>출고장비 S/N</M.Table.Th>
              <M.Table.Th>모델(모델코드)</M.Table.Th>
              <M.Table.Th>접수내용</M.Table.Th>
              <M.Table.Th>거래처</M.Table.Th>
              <M.Table.Th>접수담당자</M.Table.Th>
              <M.Table.Th>진행상태</M.Table.Th>
              <M.Table.Th>등록 담당자</M.Table.Th>
              <M.Table.Th>등록 일시</M.Table.Th>
            </M.Table.Tr>
          </M.Table.Thead>
          <M.Table.Tbody>
            {!isLoading &&
              !isError &&
              rows.map((row) => (
                <M.Table.Tr key={row.id}>
                  <M.Table.Td ta="left">
                    <M.Anchor
                      component={Link}
                      to={`/admin/management/aiagent/company/${companyId}/ascs/${row.id}${buildSearchString({
                        companyName,
                      })}`}
                      size="sm"
                      fw={600}
                      c="blue"
                    >
                      {textOrDash(row.manageNo)}
                    </M.Anchor>
                  </M.Table.Td>
                  <M.Table.Td>{formatDate(row.receiptDt)}</M.Table.Td>
                  <M.Table.Td>{textOrDash(row.receiptNo)}</M.Table.Td>
                  <M.Table.Td ta="left">{textOrDash(row.serialNumber)}</M.Table.Td>
                  <M.Table.Td ta="left">{getModelLabel(row)}</M.Table.Td>
                  <M.Table.Td maw={260}>
                    <HoverCardText value={row.receiptContent} dropdownWidth={320} />
                  </M.Table.Td>
                  <M.Table.Td maw={200}>
                    <HoverCardText value={row.customerName} dropdownWidth={280} />
                  </M.Table.Td>
                  <M.Table.Td>{textOrDash(row.receiptManagerName || row.receiptManager || row.receiptManagerEmail)}</M.Table.Td>
                  <M.Table.Td>
                    <StatusBadge status={row.currentStatus} />
                  </M.Table.Td>
                  <M.Table.Td ta="left">{textOrDash(row.regId)}</M.Table.Td>
                  <M.Table.Td>{handleChangeLocaleTime(row.regDt || '')}</M.Table.Td>
                </M.Table.Tr>
              ))}
            {(isLoading || isError || !rows.length) && (
              <M.Table.Tr>
                <M.Table.Td colSpan={9}>
                  <M.Text ta="center" c={isError ? 'red' : 'dimmed'} py="md">
                    {emptyMessage}
                  </M.Text>
                </M.Table.Td>
              </M.Table.Tr>
            )}
          </M.Table.Tbody>
        </M.Table>
      </M.Table.ScrollContainer>
      {totalCount > 0 && (
        <M.Group justify="center" mt="md">
          <M.Pagination total={totalPage} value={page} onChange={onPageChange} />
        </M.Group>
      )}
    </div>
  );
}
